import { Check } from 'lucide-react';
import type { MountStage } from '@atlas/shared';

// Ordered list of every stage in the pipeline.
export const MOUNT_ORDER: MountStage[] = [
  'rds_mounting',
  'rds_mounted',
  'syncing',
  'synced',
  'redshift_available',
  'rds_deleting',
  'rds_deleted',
];

// Each column in the table represents one logical phase.
interface PipelineCol {
  key: string;
  header: string;
  /** The step that, when reached, means this phase is "in progress". */
  activeStep: MountStage;
  /** The step that, when reached, means this phase is "done". */
  doneStep: MountStage;
  /** Label shown while the activeStep is current. */
  activeLabel: string;
  /** Label shown while the doneStep is current (before the next phase starts). */
  doneLabel: string;
  /** Pulse the active label (transient in-flight stage). */
  pulse?: boolean;
}

export const PIPELINE_COLS: PipelineCol[] = [
  {
    key: 'rds',
    header: 'RDS',
    activeStep: 'rds_mounting',
    doneStep: 'rds_mounted',
    activeLabel: 'Mounting',
    doneLabel: 'Mounted',
    pulse: true,
  },
  {
    key: 'dms',
    header: 'DMS',
    activeStep: 'syncing',
    doneStep: 'synced',
    activeLabel: 'Syncing',
    doneLabel: 'Synced',
    pulse: true,
  },
  {
    key: 'redshift',
    header: 'Redshift',
    activeStep: 'redshift_available',
    doneStep: 'redshift_available',
    activeLabel: 'Available',
    doneLabel: 'Available',
  },
  {
    key: 'rds_remove',
    header: 'RDS removable',
    activeStep: 'rds_deleting',
    doneStep: 'rds_deleted',
    activeLabel: 'Deleting',
    doneLabel: 'Deleted',
    pulse: true,
  },
];

type ColState = 'pending' | 'active' | 'done' | 'complete';

function colState(col: PipelineCol, stage: MountStage | null): ColState {
  if (!stage) return 'pending';
  const cur = MOUNT_ORDER.indexOf(stage);
  const done = MOUNT_ORDER.indexOf(col.doneStep);
  const active = MOUNT_ORDER.indexOf(col.activeStep);
  // Past the done step → complete (ticked off)
  if (cur > done) return 'complete';
  // At or past the active step up to (and including) done step → active/done
  if (cur >= active) return cur === done ? 'done' : 'active';
  return 'pending';
}

/** Single pipeline step cell rendered inside a table <td>. */
export function PipelineCell({ col, stage }: { col: PipelineCol; stage: MountStage | null }) {
  const state = colState(col, stage);

  if (state === 'complete') {
    return (
      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#0f7b6c] mx-auto">
        <Check size={12} strokeWidth={3} className="text-white" />
      </span>
    );
  }

  if (state === 'done') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#e5f5f2] text-[#0f7b6c] text-xs font-medium whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-[#0f7b6c] shrink-0" />
        {col.doneLabel}
      </span>
    );
  }

  if (state === 'active') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#e8f0fb] text-[#2383e2] text-xs font-medium whitespace-nowrap`}>
        <span className={`w-1.5 h-1.5 rounded-full bg-[#2383e2] shrink-0 ${col.pulse ? 'animate-pulse' : ''}`} />
        {col.activeLabel}
      </span>
    );
  }

  // pending
  return <span className="text-[#d3d3d1] text-base leading-none select-none flex justify-center">–</span>;
}
